import { ApiRoutes, AppPaginationOptions, BaseQueryParams, QueryError, WithPagination } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import { SearchConsoleAccount } from '@/modules/DomainsApi';
import { Site, SiteFormFields, SitesQueryKeys } from '@/modules/Sites';

export const useGetAllSitesQuery = (paginationOptions: AppPaginationOptions, search?: string) =>
  buildQuery<WithPagination<Site>, QueryError>([SitesQueryKeys.GetAll, { ...paginationOptions, search }], async () => {
    const { data } = await apiExecutor.get(ApiRoutes.Sites, { params: {
      ...paginationOptions,
      search
    } });
    return data;
  });

export const useGetContentByIdSitesQuery = (id: string) =>
  buildQuery<
  Pick<
    Site,
    | 'id'
    | 'name'
    | 'staticBaseUrl'
    | 'rootFolderId'
    | 'isAutoRegluingsEnabled'
    | 'autoRegluingsVisitor'
    | 'createdAt'
    | 'updatedAt'
    | 'deletedAt'
    | 'isSearchConsoleAddressesAutoChanging'
    | 'searchConsoleAddressChanging'
    | 'changeMainDomainProcessStatus'
    | 'visibilityOfContentUpdateDate'
    | 'contentUpdateDate'
  > & {
    searchConsoleAccount: Pick<SearchConsoleAccount, 'id' | 'email'>
  }, QueryError>([SitesQueryKeys.GetContentById, id], async () => {
    const { data } = await apiExecutor.get(ApiRoutes.SitesContentById.replace(':siteId', id));
    return data;
  });

export const useGetDossierByIdSitesQuery = (id: string) =>
  buildQuery<
  Pick<
    Site,
    | 'id'
    | 'name'
    | 'staticBaseUrl'
    | 'rootFolderId'
    | 'isAutoRegluingsEnabled'
    | 'autoRegluingsVisitor'
    | 'createdAt'
    | 'updatedAt'
    | 'deletedAt'
    | 'isSearchConsoleAddressesAutoChanging'
    | 'searchConsoleAddressChanging'
    | 'searchConsoleAccountProcessStatus'
    | 'changeMainDomainProcessStatus'
    | 'searchConsoleAccount'
    | 'visibilityOfContentUpdateDate'
    | 'contentUpdateDate'
  >, QueryError>([SitesQueryKeys.Dossier, id], async () => {
    const { data } = await apiExecutor.get(ApiRoutes.SitesById.replace(':siteId', id));
    return data;
  });

export const useCreateSitesMutation = () =>
  buildMutation<Site, QueryError, SiteFormFields>([SitesQueryKeys.Create], async (postData) => {
    const { data } = await apiExecutor.post<Site>(ApiRoutes.Sites, {
      ...postData
    }, { params: { teamId: localStorage.getItem('currentTeamId') } });
    return data;
  });

export const useEditSitesMutation = (id: string) =>
  buildMutation<Site, QueryError, SiteFormFields>([SitesQueryKeys.Edit, id], async (postData) => {
    const { data } = await apiExecutor.patch<Site>(ApiRoutes.SitesById.replace(':siteId', id), { ...postData });
    return data;
  });

export const useDeleteSitesMutation = (id: string) =>
  buildMutation<void, QueryError, void>([SitesQueryKeys.Delete, id], async () => {
    await apiExecutor.delete<void>(ApiRoutes.SitesById.replace(':siteId', id));
  });

export const useAutocompleteSitesQuery = (params: { query: string; includedIds?: string[]; excludedIds?: string[]; }) =>
  buildQuery<
    Pick<Site, 'id' | 'name'>[], QueryError
  >([SitesQueryKeys.Autocomplete, params], async () => {
    const { data } = await apiExecutor.get<Pick<Site, 'id' | 'name'>[]>(ApiRoutes.SitesAutocomplete, {
      params: {
        q: params.query,
        includedIds: params?.includedIds?.length ? String(params.includedIds) : undefined,
        excludedIds: params?.excludedIds?.length ? String(params.excludedIds) : undefined,
      }
    });
    return data;
  });

export const useBreadcrumbSitesQuery = (id: string) =>
  buildQuery<string, QueryError>([SitesQueryKeys.Breadcrumb, id], async () => {
    const { data } = await apiExecutor.get<string>(ApiRoutes.SitesBreadcrumb.replace(':siteId', id));
    return data;
  });

export const useChangeVisibilityOfContentUpdateDateMutation = (id: string) =>
  buildMutation<
  BaseQueryParams<typeof useGetDossierByIdSitesQuery>['data'], QueryError, {visibilityOfContentUpdateDate: boolean}
  >([SitesQueryKeys.ChangeVisibilityOfContentLastChangeDate, id], async (postData) => {
    const { data } = await apiExecutor.patch(ApiRoutes.SitesById.replace(':siteId', id), postData);
    return data;
  });
